import type { KassenApi } from './api';
import { bonierungAbgelehnt, ladeNachsendeListe, nachsenden } from './bonierung';
import {
  auftragsSpeicherFuerGeraet,
  exklusiv,
  raeumeAuf,
  verarbeiteWarteschlange,
  type Sender,
} from './lager-warteschlange';

/**
 * Hintergrundarbeit der Kasse (P8), unabhängig vom geöffneten Reiter:
 *
 *   Lager-Warteschlange  ausstehende Wareneingänge/Inventuren (IndexedDB, dauerhafte
 *                        Vorgangskennung) – nie doppelt gebucht, Reihenfolge bleibt
 *   Küchenbons           vorgemerkte Bonierungen nach Netzfehler (gleiche Vorgangskennung)
 *
 * Beide laufen beim Start, bei „online“ und regelmäßig. Ein Lauf je Kontext gleichzeitig
 * (`exklusiv`, auch über Tabs per Web Locks). Ergebnisse gehen als Fensterereignisse an die
 * Oberfläche: `bilanx:kuechenbons` ({offen, verworfen}) und `bilanx:lager` ({gebucht, angehalten}).
 */
export const LAGER_MS = 30_000;
export const KUECHENBON_MS = 20_000;

/** Absender der Lagerbuchungen dieser Kasse (gleich wie im Reiter „Lager“). */
export function kassenLagerSender(
  api: Pick<KassenApi, 'inventurZaehlung' | 'lagerErfassung'>,
  kassenId: string,
): Sender {
  return (k) =>
    k.laufId !== undefined && k.art === 'inventur'
      ? api.inventurZaehlung(kassenId, k.laufId, k)
      : api.lagerErfassung(kassenId, k.art, k.positionen, k.bezug, k.vorgangId, k.laufAnker);
}

export function starteHintergrund(opt: {
  api: KassenApi;
  kassenId: string;
  lager: boolean;
  kuechenbons: boolean;
  /** Ereignisse an die Oberfläche (Standard: window.dispatchEvent). */
  melde?: (art: string, daten: unknown) => void;
}): () => void {
  const { api, kassenId } = opt;
  const melde =
    opt.melde ??
    ((art: string, daten: unknown) => {
      window.dispatchEvent(new CustomEvent(art, { detail: { kassenId, ...(daten as object) } }));
    });
  let aktiv = true;
  const online = (): boolean => typeof navigator === 'undefined' || navigator.onLine;

  const lagerLauf = async (): Promise<void> => {
    if (!opt.lager || !aktiv || !online()) {
      return;
    }
    const { speicher } = auftragsSpeicherFuerGeraet();
    const kontext = `kasse:${kassenId}`;
    try {
      const r = await exklusiv(kontext, () =>
        verarbeiteWarteschlange(
          speicher,
          kontext,
          kassenLagerSender(api, kassenId),
          () => new Date(),
          null,
          () => !aktiv,
        ),
      );
      await raeumeAuf(speicher, kontext, new Date());
      if (r.gesendet > 0) {
        melde('bilanx:lager', { gebucht: r.gebucht, angehalten: r.angehalten });
      }
    } catch {
      // Ablage nicht lesbar – nächster Versuch beim nächsten Anlass
    }
  };

  let bonLaeuft = false;
  const bonLauf = async (): Promise<void> => {
    if (!opt.kuechenbons || !aktiv || !online() || bonLaeuft) {
      return;
    }
    if (ladeNachsendeListe(localStorage, kassenId).length === 0) {
      return;
    }
    bonLaeuft = true;
    try {
      const r = await nachsenden(
        (b) => api.bonieren(kassenId, b),
        localStorage,
        kassenId,
        bonierungAbgelehnt,
      );
      melde('bilanx:kuechenbons', { offen: r.offen, verworfen: r.verworfen });
    } finally {
      bonLaeuft = false;
    }
  };

  const alles = (): void => {
    void lagerLauf();
    void bonLauf();
  };
  alles();
  const t1 = setInterval(() => void lagerLauf(), LAGER_MS);
  const t2 = setInterval(() => void bonLauf(), KUECHENBON_MS);
  window.addEventListener('online', alles);
  return () => {
    aktiv = false;
    clearInterval(t1);
    clearInterval(t2);
    window.removeEventListener('online', alles);
  };
}
